/**
 * ShowOnProfileCheckboxGroup — "Show on my profile" label + a row of
 * bordered checkbox rows (Location / Birthday / Member Since), used in the
 * Information card to control per-field profile visibility.
 *
 * Builds a small custom checkbox (hidden native <input> + styled <span> box +
 * Check icon shown on :checked) rather than FormInput.v2's Checkbox — the
 * page deliberately avoids FormInput.v2's heavy padding (same carve-out as the
 * custom <select> and journey radios) and the 18×18 box / 12px gap / 16px
 * padded container can't be hit through it without `!important`.
 *
 * @route /profile/edit
 * @parent ProfileInformationCard
 */

import React from 'react'
import Check from '../../../../../Icon/icons/Check/index'

const CLS = 'community-profile-settings'

export interface ShowOnProfileCheckboxItem {
  id: string
  label: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

export interface ShowOnProfileCheckboxGroupProps {
  label: React.ReactNode
  items: ShowOnProfileCheckboxItem[]
}

function ShowOnProfileCheckboxGroup ({
  label,
  items,
}: ShowOnProfileCheckboxGroupProps) {
  return (
    <div className={`${CLS}__show-on-profile`}>
      <span className={`${CLS}__show-on-profile-label`}>{label}</span>
      <div className={`${CLS}__show-on-profile-list`}>
        {items.map((item) => {
          const inputId = `show-on-${item.id}`
          return (
            <label
              key={item.id}
              htmlFor={inputId}
              className={`${CLS}__show-on-profile-checkbox${
                item.checked ? ` ${CLS}__show-on-profile-checkbox--checked` : ''
              }${item.disabled ? ` ${CLS}__show-on-profile-checkbox--disabled` : ''}`}
            >
              <input
                id={inputId}
                type="checkbox"
                className={`${CLS}__show-on-profile-input`}
                checked={item.checked}
                disabled={item.disabled}
                onChange={(e) => item.onChange(e.target.checked)}
              />
              <span className={`${CLS}__show-on-profile-box`} aria-hidden="true">
                <Check size="14px" className={`${CLS}__show-on-profile-check`} />
              </span>
              <span className={`${CLS}__show-on-profile-checkbox-label`}>{item.label}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export default ShowOnProfileCheckboxGroup
